import z from 'zod';

// ---------------------------------------------------------------------------
// Workforce inbound — events we receive
// apps/core-backend/src/adapters/outbound/messaging/nats/external/workforce-external-event.schema.ts
// ---------------------------------------------------------------------------

const nonEmptyString = z.string().min(1);

// job.saved (workforce.v1.external.job.{jobId}.saved)
// ---------------------------------------------------------------------------

// Tower mapping blocks — identifiers only on the wire (spec AC-021).
const jobSavedTowerDataSchema = z.object({
	id: nonEmptyString,
});

const jobSavedTowerEntityRefSchema = z.object({
	id: nonEmptyString,
	towerId: z.string().nullable(), // null when no Tower mapping
	towerData: jobSavedTowerDataSchema.nullable(),
});

const siteTowerDataSchema = jobSavedTowerDataSchema;

const siteRefSchema = z.object({
	id: nonEmptyString,
	name: nonEmptyString,
	address: z.object({
		line: z.string().nullable(),
		postalCode: z.string().nullable(),
		city: z.string().nullable(),
		country: z.string().nullable(),
	}),
	towerId: nonEmptyString,
	towerData: siteTowerDataSchema.nullable(),
});

const zoneTowerDataSchema = jobSavedTowerDataSchema;

const zoneRefSchema = z.object({
	id: nonEmptyString,
	name: nonEmptyString,
	towerId: nonEmptyString,
	towerData: zoneTowerDataSchema.nullable(),
});

const clientTowerDataSchema = z.object({
	id: nonEmptyString,
	url: nonEmptyString.nullable(),
});

const clientRefSchema = z.object({
	id: nonEmptyString,
	name: nonEmptyString,
	towerId: nonEmptyString,
	towerData: clientTowerDataSchema.nullable(),
});

const workforceContractorCompanyRefSchema = z.object({
	id: nonEmptyString, // Workforce ContractorCompanyId
	name: nonEmptyString,
	registrationNumber: nonEmptyString, // VAT or equivalent — the cross-system key
});

const workforceJobCoreSchema = z.object({
	id: nonEmptyString,
	title: nonEmptyString,
	description: z.string().nullable(), // Job.description is optional in domain; null on the wire when absent.
	// ISO-8601 with explicit UTC offset on the wire (spec AC-010).
	startTime: z.coerce.date(),
	finishTime: z.coerce.date(),
	// Lifecycle timestamps — initial save when `createdAt === updatedAt`,
	// subsequent saves bump `updatedAt` only (spec AC-007, AC-010).
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date(),
	status: z.enum(['draft', 'requested', 'info_needed', 'accepted', 'confirmed', 'canceled', 'completed']),
	origin: z.enum(['workforce', 'tower']), // mirrors job.aggregate.ts JobOrigin
	towerId: z.string().nullable(),
	towerData: jobSavedTowerDataSchema.nullable(),
	// Null when the job is a root job. Present block when it is a subjob.
	parent: jobSavedTowerEntityRefSchema.nullable(),
	// The top-level job in the hierarchy. Null when this job is the root.
	root: jobSavedTowerEntityRefSchema.nullable(),
});

export const jobSavedExternalEventSchema = z.object({
	event: z.literal('job.saved'),
	// Unique per emission. Re-emissions of the same (jobId, updatedAt) carry different eventIds.
	eventId: nonEmptyString,
	// Wall-clock time the event was emitted (not the save time — that lives on job.updatedAt).
	emittedAt: z.coerce.date(),
	data: z.object({
		job: workforceJobCoreSchema,
		contractorCompany: workforceContractorCompanyRefSchema, // assigned contractor company
		site: siteRefSchema.nullable(),
		// Spec AC-015: present and possibly empty, never absent and never null.
		zones: z.array(zoneRefSchema),
		client: clientRefSchema.nullable(),
	}),
});

// worker.assigned (workforce.v1.external.assignment.*)
// ---------------------------------------------------------------------------

// Tower data blocks — narrow by design (spec AC-021): identifiers, plus the reference URL on the
// client only. Workforce does not re-fetch or enrich Tower-side data at emission time.
const workerAssignedJobTowerDataSchema = z.object({
	id: nonEmptyString, // id only — not the full job snapshot (spec AC-015)
});

const workerAssignedClientTowerDataSchema = z.object({
	id: nonEmptyString,
	url: nonEmptyString.nullable(),
});

const workerAssignedSiteTowerDataSchema = z.object({
	id: nonEmptyString,
});

// Mirrors the JobRole value object (domain/value-objects/job-role.vo.ts).
const workerAssignedRoleRefSchema = z.object({
	id: nonEmptyString,
	name: nonEmptyString,
});

const workerAssignedWorkerRefSchema = z.object({
	id: nonEmptyString,
	firstName: nonEmptyString,
	lastName: nonEmptyString,
	nationality: z.string().nullable(),
	dateOfBirth: z.coerce.date().nullable(),
});

const workerAssignedEmploymentRefSchema = z.object({
	id: nonEmptyString,
	// Contact channels flattened onto the employment (per-company contact). For Tower-rooted jobs at
	// least one of email/phone is non-null (enforced upstream); workforce-only jobs may carry both null.
	email: z.string().nullable(),
	phone: z.string().nullable(),
});

const workerAssignedContractorCompanyRefSchema = z.object({
	id: nonEmptyString,
	name: nonEmptyString,
	registrationNumber: nonEmptyString, // VAT or equivalent — the cross-system key (spec AC-014)
});

const workerAssignedJobRefSchema = z.object({
	id: nonEmptyString,
	title: nonEmptyString,
	status: z.enum(['draft', 'requested', 'info_needed', 'accepted', 'confirmed', 'canceled', 'completed']),
	origin: z.enum(['workforce', 'tower']),
	startTime: z.coerce.date(),
	finishTime: z.coerce.date(),
	towerData: workerAssignedJobTowerDataSchema.nullable(),
});

const workerAssignedClientRefSchema = z.object({
	id: nonEmptyString,
	name: nonEmptyString,
	towerData: workerAssignedClientTowerDataSchema.nullable(),
});

const workerAssignedSiteRefSchema = z.object({
	id: nonEmptyString,
	name: nonEmptyString,
	towerData: workerAssignedSiteTowerDataSchema.nullable(),
});

const workerAssignedAssignmentCoreSchema = z.object({
	id: nonEmptyString,
	assignedAt: z.coerce.date(), // creation moment; ISO-8601 + UTC offset (spec AC-010)
	updatedAt: z.coerce.date(), // this-save moment; == assignedAt on the initial assignment. Feeds the dedupe key.
	roles: z.array(workerAssignedRoleRefSchema), // always present; empty when no roles (spec AC-013)
});

export const workerAssignedExternalEventSchema = z.object({
	event: z.literal('worker.assigned'),
	eventId: nonEmptyString, // unique per emission; log correlation only, never the dedupe key
	emittedAt: z.coerce.date(),
	data: z.object({
		assignment: workerAssignedAssignmentCoreSchema,
		worker: workerAssignedWorkerRefSchema,
		employment: workerAssignedEmploymentRefSchema,
		contractorCompany: workerAssignedContractorCompanyRefSchema, // the employing / assigning company
		job: workerAssignedJobRefSchema,
		client: workerAssignedClientRefSchema.nullable(), // null for standalone workforce jobs (spec AC-017)
		site: workerAssignedSiteRefSchema.nullable(), // null for standalone workforce jobs (spec AC-018)
	}),
});

// worker.unassigned (workforce.v1.external.assignment.*)
// ---------------------------------------------------------------------------

const workerUnassignedTowerDataSchema = z.object({
	id: nonEmptyString,
});

const workerUnassignedTowerEntityRefSchema = z.object({
	id: nonEmptyString,
	towerId: nonEmptyString,
	towerData: workerUnassignedTowerDataSchema,
});

const workerUnassignedWorkerRefSchema = z.object({
	id: nonEmptyString,
	firstName: nonEmptyString,
	lastName: nonEmptyString,
});

const workerUnassignedContractorCompanyRefSchema = z.object({
	id: nonEmptyString,
	registrationNumber: nonEmptyString, // VAT or equivalent — the cross-system key
});

const workerUnassignedJobRefSchema = z.object({
	id: nonEmptyString,
	towerId: nonEmptyString,
	towerData: workerUnassignedTowerDataSchema,
});

const workerUnassignedAssignmentCoreSchema = z.object({
	id: nonEmptyString,
	unassignedAt: z.coerce.date(), // ISO-8601 + UTC offset
});

export const workerUnassignedExternalEventSchema = z.object({
	event: z.literal('worker.unassigned'),
	eventId: nonEmptyString,
	emittedAt: z.coerce.date(),
	data: z.object({
		assignment: workerUnassignedAssignmentCoreSchema,
		worker: workerUnassignedWorkerRefSchema,
		contractorCompany: workerUnassignedContractorCompanyRefSchema,
		job: workerUnassignedJobRefSchema,
		client: workerUnassignedTowerEntityRefSchema,
		site: workerUnassignedTowerEntityRefSchema,
	}),
});

/** Discriminant values for workforce inbound messages. */
export const workforceExternalEventTypes = ['job.saved', 'worker.assigned', 'worker.unassigned'] as const;

export type WorkforceExternalEventType = (typeof workforceExternalEventTypes)[number];

const workforceExternalEventTypeSet = new Set<string>(workforceExternalEventTypes);

export function isKnownWorkforceExternalEventType(event: string): event is WorkforceExternalEventType {
	return workforceExternalEventTypeSet.has(event);
}

/** Reads only the `event` discriminant before full validation. */
export const workforceExternalEventDiscriminantSchema = z.object({
	event: z.string(),
});

/**
 * Payload shape is determined by `event`. Use `parseWorkforceExternalEvent()` at the consumer:
 * known types are fully validated; unknown types are returned without throwing.
 */
export const workforceExternalEventSchema = z.discriminatedUnion('event', [
	jobSavedExternalEventSchema,
	workerAssignedExternalEventSchema,
	workerUnassignedExternalEventSchema,
]);

export type ParseWorkforceExternalEventResult =
	| { kind: 'known'; event: z.infer<typeof workforceExternalEventSchema> }
	| { kind: 'unknown'; eventType: string };

export function parseWorkforceExternalEvent(payload: unknown): ParseWorkforceExternalEventResult {
	const discriminant = workforceExternalEventDiscriminantSchema.safeParse(payload);
	if (!discriminant.success) {
		throw discriminant.error;
	}

	const { event: eventType } = discriminant.data;
	if (!isKnownWorkforceExternalEventType(eventType)) {
		return { kind: 'unknown', eventType };
	}

	return {
		kind: 'known',
		event: workforceExternalEventSchema.parse(payload),
	};
}
